// ── Settings Menu ──
// In-game settings overlay with sections: Camera, Sensitivity, TPP, Crosshair,
// Graphics Quality, Audio, FOV, Resolution, Shadow/Texture Quality, and Minimap.
// Follows the BuyMenu overlay pattern.

import { settingsStore, type CameraMode, type GameSettings, type GraphicsQuality, type UiTheme } from '../core/settings';
import { type CrosshairConfig, generateCrosshairRenderData } from './crosshair-settings';
import { audioManager } from '../audio/audio-manager';
import { announcer } from '../audio/announcer';
import { musicSystem } from '../audio/music';
import { voiceChat } from '../audio/voice';
import { playUIHover } from '../audio/sounds';
import { KeybindEditor } from './keybind-editor';
import { ColorblindMode, applyColorblindCSS } from '../renderer/colorblind';
import { showGyroPermissionBanner } from './gyro-permission-banner';
import { isMobile } from '../core/device';

// ── MOB-W6: weapon options for the quick-buy customization dropdowns ──
// Hard-coded list of the typical buyable weapons (pistols/rifles/snipers/SMGs)
// keyed by weapon id, matching the BuyMenu's WEAPONS table. We don't depend
// on core/config's full WEAPONS dict here so the settings menu stays
// independent of the runtime weapon registry; new weapons just need to be
// added to this list when they ship.
export const QUICK_BUY_WEAPON_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'glock', label: 'Glock-18 ($200)' },
  { id: 'usp', label: 'USP-S ($200)' },
  { id: 'p250', label: 'P250 ($300)' },
  { id: 'fiveseven', label: 'Five-SeveN ($500)' },
  { id: 'tec9', label: 'Tec-9 ($500)' },
  { id: 'deagle', label: 'Desert Eagle ($700)' },
  { id: 'mp9', label: 'MP9 ($1250)' },
  { id: 'mac10', label: 'MAC-10 ($1050)' },
  { id: 'p90', label: 'P90 ($2350)' },
  { id: 'nova', label: 'Nova ($1200)' },
  { id: 'ssg08', label: 'SSG 08 ($1700)' },
  { id: 'galil', label: 'Galil AR ($1800)' },
  { id: 'famas', label: 'FAMAS ($2050)' },
  { id: 'ak47', label: 'AK-47 ($2700)' },
  { id: 'm4a4', label: 'M4A4 ($3100)' },
  { id: 'm4a1s', label: 'M4A1-S ($2900)' },
  { id: 'aug', label: 'AUG ($3300)' },
  { id: 'sg553', label: 'SG 553 ($3000)' },
  { id: 'awp', label: 'AWP ($4750)' },
];

// ── Extended Settings (stored separately from core settings) ──

export type ShadowQuality = 'off' | 'low' | 'medium' | 'high';
export type TextureQuality = 'low' | 'medium' | 'high';

export interface ExtendedSettings {
  fov: number;
  shadowQuality: ShadowQuality;
  textureQuality: TextureQuality;
  minimapOpacity: number;
  minimapScale: number;
  minimapShowTeammates: boolean;
}

export const EXTENDED_SETTINGS_KEY = 'counter_pump_extended_settings';

// FOV constants
export const FOV_MIN = 60;
export const FOV_MAX = 120;
export const FOV_DEFAULT = 90;
export const FOV_STEP = 5;

// Minimap constants
export const MINIMAP_OPACITY_MIN = 0.2;
export const MINIMAP_OPACITY_MAX = 1.0;
export const MINIMAP_OPACITY_DEFAULT = 0.7;
export const MINIMAP_SCALE_MIN = 0.5;
export const MINIMAP_SCALE_MAX = 2.0;
export const MINIMAP_SCALE_DEFAULT = 1.0;

export const SHADOW_QUALITY_OPTIONS: Array<{ label: string; value: ShadowQuality }> = [
  { label: 'Off', value: 'off' },
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

export const TEXTURE_QUALITY_OPTIONS: Array<{ label: string; value: TextureQuality }> = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

export const EXTENDED_SETTINGS_DEFAULTS: ExtendedSettings = {
  fov: FOV_DEFAULT,
  shadowQuality: 'medium',
  textureQuality: 'medium',
  minimapOpacity: MINIMAP_OPACITY_DEFAULT,
  minimapScale: MINIMAP_SCALE_DEFAULT,
  minimapShowTeammates: true,
};

export function clampFov(val: number): number {
  return Math.max(FOV_MIN, Math.min(FOV_MAX, val));
}

export function clampMinimapOpacity(val: number): number {
  return Math.max(MINIMAP_OPACITY_MIN, Math.min(MINIMAP_OPACITY_MAX, val));
}

export function clampMinimapScale(val: number): number {
  return Math.max(MINIMAP_SCALE_MIN, Math.min(MINIMAP_SCALE_MAX, val));
}

/** Maps shadow quality to shadow map resolution. */
export function shadowMapSize(quality: ShadowQuality): number {
  switch (quality) {
    case 'low': return 512;
    case 'medium': return 1024;
    case 'high': return 2048;
    default: return 0;
  }
}

export function loadExtendedSettings(): ExtendedSettings {
  try {
    const raw = localStorage.getItem(EXTENDED_SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ExtendedSettings>;
      return { ...EXTENDED_SETTINGS_DEFAULTS, ...parsed };
    }
  } catch {
    // Corrupted data — fall back to defaults
  }
  return { ...EXTENDED_SETTINGS_DEFAULTS };
}

export function saveExtendedSettings(settings: ExtendedSettings): void {
  try {
    localStorage.setItem(EXTENDED_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/** Preset crosshair colors. */
const COLOR_PRESETS = [
  { label: 'Green', value: '#00ff00' },
  { label: 'Cyan', value: '#00ffff' },
  { label: 'Red', value: '#ff0000' },
  { label: 'Yellow', value: '#ffff00' },
  { label: 'White', value: '#ffffff' },
  { label: 'Pink', value: '#ff66cc' },
];

type SettingsSectionId =
  | 'camera'
  | 'sensitivity'
  | 'tpp'
  | 'graphics'
  | 'audio'
  | 'accessibility'
  | 'aim'
  | 'touch'
  | 'quick-buy'
  | 'gyro'
  | 'hud'
  | 'crosshair'
  | 'fov'
  | 'display'
  | 'shadows'
  | 'textures'
  | 'minimap'
  | 'controls';

const SETTINGS_NAV_SECTIONS: Array<{ id: SettingsSectionId; label: string }> = [
  { id: 'camera', label: 'Camera' },
  { id: 'sensitivity', label: 'Sensitivity' },
  { id: 'tpp', label: 'TPP' },
  { id: 'graphics', label: 'Graphics' },
  { id: 'audio', label: 'Audio' },
  { id: 'accessibility', label: 'Accessibility' },
  { id: 'aim', label: 'Aim' },
  { id: 'touch', label: 'Touch' },
  { id: 'quick-buy', label: 'Quick-Buy' },
  { id: 'gyro', label: 'Gyro' },
  { id: 'hud', label: 'HUD' },
  { id: 'crosshair', label: 'Crosshair' },
  { id: 'fov', label: 'FOV' },
  { id: 'display', label: 'Display' },
  { id: 'shadows', label: 'Shadows' },
  { id: 'textures', label: 'Textures' },
  { id: 'minimap', label: 'Minimap' },
  { id: 'controls', label: 'Controls' },
];

export class SettingsMenu {
  private overlay: HTMLElement;
  private open = false;
  private onReplayMobileTutorial: (() => void) | null = null;
  private boundKeyHandler: (e: KeyboardEvent) => void;

  // UI element references — Camera
  private fppBtn!: HTMLElement;
  private tppBtn!: HTMLElement;
  private sensitivitySlider!: HTMLInputElement;
  private sensitivityValue!: HTMLElement;
  private distanceSlider!: HTMLInputElement;
  private distanceValue!: HTMLElement;
  private heightSlider!: HTMLInputElement;
  private heightValue!: HTMLElement;
  private shoulderToggle!: HTMLElement;

  // Crosshair section
  private crosshairPreviewCanvas!: HTMLCanvasElement;

  // Audio section
  private masterVolumeSlider!: HTMLInputElement;
  private masterVolumeValue!: HTMLElement;
  private sfxVolumeSlider!: HTMLInputElement;
  private sfxVolumeValue!: HTMLElement;
  private musicVolumeSlider!: HTMLInputElement;
  private musicVolumeValue!: HTMLElement;
  private voiceVolumeSlider!: HTMLInputElement;
  private voiceVolumeValue!: HTMLElement;
  private announcerVolumeSlider!: HTMLInputElement;
  private announcerVolumeValue!: HTMLElement;
  private footstepVolumeSlider!: HTMLInputElement;
  private footstepVolumeValue!: HTMLElement;
  // BUG-P-16: microphone device picker
  private micDeviceSelect: HTMLSelectElement | null = null;
  private micPermissionNote: HTMLElement | null = null;
  private micRetryBtn: HTMLButtonElement | null = null;

  // Graphics section
  private qualityBtns: HTMLElement[] = [];

  // Accessibility
  private colorblindSelect!: HTMLSelectElement;
  private adsSnapToggle: HTMLElement | null = null;
  private controlModeSelect: HTMLSelectElement | null = null;

  // Keybind editor (lazy — survives buildUI re-runs so listeners are not leaked)
  private keybindEditor: KeybindEditor | null = null;

  // Extended settings (FOV, shadow, texture, minimap)
  private extendedSettings: ExtendedSettings;
  private fovSlider!: HTMLInputElement;
  private fovValue!: HTMLElement;
  private shadowBtns: HTMLElement[] = [];
  private textureBtns: HTMLElement[] = [];
  private minimapOpacitySlider!: HTMLInputElement;
  private minimapOpacityValue!: HTMLElement;
  private minimapScaleSlider!: HTMLInputElement;
  private minimapScaleValue!: HTMLElement;
  private minimapTeammatesCheckbox!: HTMLInputElement;

  /** F-MOB-H1 / F-MOB-M6: callbacks injected by game.ts to bridge settings UI
   *  with the running TouchInputProvider. */
  private onRequestGyroPermission: (() => Promise<boolean> | boolean) | null = null;
  private onCalibrateGyro: (() => void) | null = null;
  /** MOB-W5 / F-MOB-M5: callback to open the HUD layout editor. */
  private onOpenHudEditor: (() => void) | null = null;

  /** MOB-W6: read the current quick-buy loadout (3 weapon ids). The settings
   *  menu uses this to populate the dropdowns each time it opens. Returns
   *  null when no QuickBuyController has been registered (desktop). */
  private getQuickBuyLoadout: (() => string[]) | null = null;
  /** MOB-W6: notify the host that a single slot's weapon changed. The host
   *  is responsible for persisting + refreshing the button labels. */
  private onQuickBuySlotChange: ((slot: 0 | 1 | 2, weaponId: string) => void) | null = null;

  constructor() {
    this.extendedSettings = loadExtendedSettings();
    this.overlay = document.createElement('div');
    this.overlay.id = 'settings-menu';
    this.overlay.style.cssText =
      'position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; ' +
      'background: rgba(0,0,0,0.85); z-index: 70; display: none; ' +
      'font-family: "Courier New", monospace; color: #ffffff; user-select: none; ' +
      'justify-content: center; align-items: center;';
    document.body.appendChild(this.overlay);

    // BUG-P-8: range inputs must declare touch-action so iOS Safari does not
    // interpret the slider drag as a page-scroll gesture. Scoped to this
    // overlay so we don't affect other pages.
    SettingsMenu.injectScopedStyles();

    // Apply persisted theme class to body on first construction so the saved
    // choice is honored even before the user opens the menu.
    SettingsMenu.applyThemeClass(settingsStore.get().theme);

    this.buildUI();

    this.boundKeyHandler = (e: KeyboardEvent) => {
      if (e.code === 'Escape' && this.open) {
        e.stopPropagation();
        this.hide();
      }
    };
    document.addEventListener('keydown', this.boundKeyHandler, true);
  }

  // ---------------------------------------------------------------------------
  // UI construction
  // ---------------------------------------------------------------------------

  private buildUI(): void {
    const settings = settingsStore.get();
    const mobile = isMobile();
    // BUG-P-3 / BUG-P-2: drop the hard 520px min-width so the card fits a
    // portrait phone (~360 px). Use a fluid width with viewport padding.
    // BUG-P-17: render a tap-target "× CLOSE" button on mobile (top-right
    // of the card) — desktop still uses ESC.
    const closeButtonHtml = mobile
      ? `<div id="settings-menu-close-btn" role="button" aria-label="Close settings" style="
            position: absolute; top: 10px; right: 10px;
            min-width: 44px; min-height: 44px;
            padding: 8px 14px; font-size: 18px; line-height: 1;
            background: rgba(60, 60, 50, 0.6);
            border: 1px solid #3a3a2a; border-radius: 4px;
            color: #c8c090; cursor: pointer; user-select: none;
            display: flex; align-items: center; justify-content: center;
            font-family: 'Courier New', monospace; letter-spacing: 1px;
            transition: background 0.15s, color 0.15s;
          ">× CLOSE</div>`
      : '';

    this.overlay.innerHTML = `
      <div style="
        position: relative;
        background: rgba(20, 24, 20, 0.95);
        border: 1px solid #3a3a2a;
        border-radius: 4px;
        padding: 28px 36px;
        width: min(580px, calc(100vw - 24px));
        max-height: 90vh;
        overflow-y: auto;
        box-sizing: border-box;
      ">
        ${closeButtonHtml}
        <h2 style="
          text-align: center; margin: 0 0 20px;
          font-size: 18px; color: #c8c090;
          letter-spacing: 4px; text-transform: uppercase;
        ">SETTINGS</h2>
        <div style="margin-bottom: 12px;">
          <input id="settings-search-input" type="search" placeholder="Search settings…" aria-label="Search settings"
            style="
              width: 100%; box-sizing: border-box;
              background: rgba(40,40,30,0.9); color: #c8c090;
              border: 1px solid #3a3a2a; border-radius: 3px;
              padding: 8px 12px; font-size: 12px;
              font-family: 'Courier New', monospace;
              letter-spacing: 1px; outline: none;
            " />
        </div>
        ${this.settingsTabStripHtml()}

        <!-- ═══════ Camera Mode ═══════ -->
        <div data-settings-section="camera" style="margin-bottom: 20px;">
          ${this.sectionLabel('CAMERA PERSPECTIVE')}
          <div style="display: flex; gap: 8px;">
            <div id="settings-fpp-btn" style="
              flex: 1; padding: 10px 0; text-align: center;
              font-size: 14px; font-weight: bold; letter-spacing: 2px;
              cursor: pointer; border-radius: 3px; transition: all 0.15s;
            ">FIRST PERSON</div>
            <div id="settings-tpp-btn" style="
              flex: 1; padding: 10px 0; text-align: center;
              font-size: 14px; font-weight: bold; letter-spacing: 2px;
              cursor: pointer; border-radius: 3px; transition: all 0.15s;
            ">THIRD PERSON</div>
          </div>
          <div style="font-size: 10px; color: #555; margin-top: 6px; text-align: center;">
            Press <span style="color: #c8c090;">V</span> in-game to toggle
          </div>
        </div>

        <!-- ═══════ Sensitivity ═══════ -->
        <div data-settings-section="sensitivity" style="margin-bottom: 16px;">
          ${this.sliderHeader('SENSITIVITY', 'settings-sens-val', settings.sensitivity.toFixed(4))}
          <input id="settings-sens-slider" type="range" min="0.0005" max="0.01" step="0.0001"
            value="${settings.sensitivity}"
            style="width: 100%; accent-color: #e94560; cursor: pointer;" />
        </div>

        <!-- ═══════ TPP Settings ═══════ -->
        <div id="settings-tpp-group" data-settings-section="tpp" style="
          border-top: 1px solid #2a2a2a; padding-top: 16px;
          transition: opacity 0.2s;
          ${settings.cameraMode === 'fpp' ? 'opacity: 0.4; pointer-events: none;' : ''}
        ">
          ${this.sectionLabel('THIRD PERSON OPTIONS', '11px', '#666')}

          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Distance', 'settings-dist-val', settings.tppDistance.toFixed(1) + 'm', '11px')}
            <input id="settings-dist-slider" type="range" min="1.5" max="6" step="0.1"
              value="${settings.tppDistance}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Height', 'settings-height-val', settings.tppHeight.toFixed(1) + 'm', '11px')}
            <input id="settings-height-slider" type="range" min="0" max="2.5" step="0.1"
              value="${settings.tppHeight}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 4px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 11px; color: #777;">Shoulder View</span>
              <div id="settings-shoulder-toggle" style="
                padding: 4px 14px; font-size: 12px;
                background: rgba(80, 90, 60, 0.5);
                border: 1px solid #4a5a3a;
                border-radius: 3px; cursor: pointer;
                color: #c8c090; letter-spacing: 1px;
                transition: all 0.15s;
              ">${settings.tppShoulder >= 0 ? 'RIGHT' : 'LEFT'}</div>
            </div>
          </div>
        </div>

        <!-- ═══════ Graphics ═══════ -->
        <div data-settings-section="graphics" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('GRAPHICS QUALITY')}
          <div id="settings-quality-group" style="display: flex; gap: 6px; margin-top: 8px;">
            ${(['low', 'medium', 'high', 'ultra'] as GraphicsQuality[])
              .map(
                (q) => `<div class="quality-btn" data-quality="${q}" style="
                  flex: 1; padding: 8px 0; text-align: center;
                  font-size: 12px; font-weight: bold; letter-spacing: 1px;
                  cursor: pointer; border-radius: 3px; transition: all 0.15s;
                  text-transform: uppercase;
                ">${q}</div>`,
              )
              .join('')}
          </div>
        </div>

        <!-- ═══════ Audio ═══════ -->
        <div data-settings-section="audio" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('AUDIO')}

          <div style="margin-bottom: 12px; margin-top: 8px;">
            ${this.sliderHeader('Master Volume', 'settings-master-vol-val', Math.round(settings.masterVolume * 100) + '%', '11px')}
            <input id="settings-master-vol-slider" type="range" min="0" max="1" step="0.01"
              value="${settings.masterVolume}"
              style="width: 100%; accent-color: #c8c090; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('SFX Volume', 'settings-sfx-vol-val', Math.round(settings.sfxVolume * 100) + '%', '11px')}
            <input id="settings-sfx-vol-slider" type="range" min="0" max="1" step="0.01"
              value="${settings.sfxVolume}"
              style="width: 100%; accent-color: #c8c090; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Music Volume', 'settings-music-vol-val', Math.round(settings.musicVolume * 100) + '%', '11px')}
            <input id="settings-music-vol-slider" type="range" min="0" max="1" step="0.01"
              value="${settings.musicVolume}"
              style="width: 100%; accent-color: #c8c090; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Voice Volume', 'settings-voice-vol-val', Math.round(settings.voiceVolume * 100) + '%', '11px')}
            <input id="settings-voice-vol-slider" type="range" min="0" max="1" step="0.01"
              value="${settings.voiceVolume}"
              style="width: 100%; accent-color: #c8c090; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Announcer Volume', 'settings-announcer-vol-val', Math.round(settings.announcerVolume * 100) + '%', '11px')}
            <input id="settings-announcer-vol-slider" type="range" min="0" max="1" step="0.01"
              value="${settings.announcerVolume}"
              style="width: 100%; accent-color: #c8c090; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Footstep Volume', 'settings-footstep-vol-val', Math.round(settings.footstepVolume * 100) + '%', '11px')}
            <input id="settings-footstep-vol-slider" type="range" min="0" max="1" step="0.01"
              value="${settings.footstepVolume}"
              style="width: 100%; accent-color: #c8c090; cursor: pointer;" />
          </div>

          <!-- BUG-P-16: Microphone Input device picker -->
          <div style="margin-bottom: 4px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
              <span style="font-size: 11px; color: #888; letter-spacing: 1px;">Microphone Input</span>
            </div>
            <select id="settings-mic-device-select" style="
              width: 100%; background: rgba(40,40,30,0.9); color: #c8c090;
              border: 1px solid #3a3a2a; border-radius: 3px; padding: 6px 10px;
              font-size: 12px; font-family: 'Courier New', monospace; cursor: pointer;
              outline: none;
            ">
              <option value="">System default</option>
            </select>
            <div id="settings-mic-permission-note" style="
              display: none; margin-top: 6px; padding: 6px 8px;
              background: rgba(60,40,30,0.6); border: 1px solid #6a4a2a;
              border-radius: 3px; font-size: 10px; color: #c8a070;
              justify-content: space-between; align-items: center; gap: 8px;
            ">
              <span>Grant mic permission to choose a device</span>
              <button id="settings-mic-retry-btn" type="button" style="
                background: rgba(80,60,40,0.9); color: #c8c090;
                border: 1px solid #6a4a2a; border-radius: 3px;
                padding: 3px 10px; font-size: 10px; font-family: 'Courier New', monospace;
                cursor: pointer; letter-spacing: 1px;
              ">RETRY</button>
            </div>
          </div>
        </div>

        <!-- ═══════ Accessibility ═══════ -->
        <div data-settings-section="accessibility" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('ACCESSIBILITY')}
          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
            <span style="font-size: 11px; color: #888; letter-spacing: 1px;">Colorblind Mode</span>
            <select id="settings-colorblind-select" style="
              background: rgba(40,40,30,0.9); color: #c8c090; border: 1px solid #3a3a2a;
              border-radius: 3px; padding: 6px 10px; font-size: 12px;
              font-family: 'Courier New', monospace; cursor: pointer;
              outline: none;
            ">
              <option value="${ColorblindMode.None}" ${settings.colorblindMode === ColorblindMode.None ? 'selected' : ''}>None</option>
              <option value="${ColorblindMode.Deuteranopia}" ${settings.colorblindMode === ColorblindMode.Deuteranopia ? 'selected' : ''}>Deuteranopia (Red-Green)</option>
              <option value="${ColorblindMode.Protanopia}" ${settings.colorblindMode === ColorblindMode.Protanopia ? 'selected' : ''}>Protanopia (Red-Green Alt)</option>
              <option value="${ColorblindMode.Tritanopia}" ${settings.colorblindMode === ColorblindMode.Tritanopia ? 'selected' : ''}>Tritanopia (Blue-Yellow)</option>
            </select>
          </div>
        </div>

        <!-- ═══════ Aim & Controls ═══════ -->
        <!-- C9 — surfaces adsSnapEnabled + controlMode that previously had
             store fields but no UI. scopeSensitivity is per-scope-type and
             stays a power-user setting (configured via console for now). -->
        <div data-settings-section="aim" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('AIM & CONTROLS')}

          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
            <span style="font-size: 11px; color: #888; letter-spacing: 1px;">ADS Snap to Hipfire</span>
            <div id="settings-ads-snap-toggle" style="
              padding: 4px 14px; font-size: 12px;
              background: rgba(80, 90, 60, 0.5);
              border: 1px solid #4a5a3a;
              border-radius: 3px; cursor: pointer;
              color: #c8c090; letter-spacing: 1px;
              transition: all 0.15s;
            ">${settings.adsSnapEnabled ? 'ON' : 'OFF'}</div>
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 12px;">
            <span style="font-size: 11px; color: #888; letter-spacing: 1px;">Control Mode</span>
            <select id="settings-control-mode-select" style="
              background: rgba(40,40,30,0.9); color: #c8c090; border: 1px solid #3a3a2a;
              border-radius: 3px; padding: 6px 10px; font-size: 12px;
              font-family: 'Courier New', monospace; cursor: pointer;
              outline: none;
            ">
              <option value="simple" ${settings.controlMode === 'simple' ? 'selected' : ''}>Simple</option>
              <option value="advanced" ${settings.controlMode === 'advanced' ? 'selected' : ''}>Advanced</option>
            </select>
          </div>
        </div>

        <!-- ═══════ Touch Controls (MOB-W5 / MOB-L2) ═══════ -->
        <div data-settings-section="touch" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('TOUCH CONTROLS')}

          <div style="margin-bottom: 12px; margin-top: 8px;">
            ${this.sliderHeader('Joystick Deadzone', 'settings-touch-deadzone-val', settings.touchJoystickDeadzone.toFixed(2), '11px')}
            <input id="settings-touch-deadzone-slider" type="range" min="0" max="0.5" step="0.01"
              value="${settings.touchJoystickDeadzone}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <!-- F-MOB-M1 / MOB-W8: per-scope sensitivity multipliers (hipfire / reddot / scope4x). -->
          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Hip Sensitivity (1.0x)', 'settings-sens-hip-val', (settings.scopeSensitivity?.hipfire ?? 1.0).toFixed(2), '11px')}
            <input id="settings-sens-hip-slider" type="range" min="0.1" max="2" step="0.05"
              value="${settings.scopeSensitivity?.hipfire ?? 1.0}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>
          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Red Dot Sensitivity (0.8x)', 'settings-sens-reddot-val', (settings.scopeSensitivity?.reddot ?? 0.8).toFixed(2), '11px')}
            <input id="settings-sens-reddot-slider" type="range" min="0.1" max="2" step="0.05"
              value="${settings.scopeSensitivity?.reddot ?? 0.8}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>
          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Scoped Sensitivity (0.3x)', 'settings-sens-scoped-val', (settings.scopeSensitivity?.scope4x ?? 0.3).toFixed(2), '11px')}
            <input id="settings-sens-scoped-slider" type="range" min="0.1" max="1.5" step="0.05"
              value="${settings.scopeSensitivity?.scope4x ?? 0.3}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>
        </div>

        <!-- ═══════ MOB-W6: Quick-Buy slots ═══════ -->
        <div data-settings-section="quick-buy" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;" data-section="quick-buy">
          ${this.sectionLabel('QUICK-BUY')}
          <div style="font-size: 10px; color: #555; margin-bottom: 10px;">
            Three weapon shortcuts shown during freeze time on mobile.
          </div>
          ${this.renderQuickBuySlot(0)}
          ${this.renderQuickBuySlot(1)}
          ${this.renderQuickBuySlot(2)}
        </div>

        <!-- ═══════ Gyro Aim (F-MOB-H1 / F-MOB-M6) ═══════ -->
        <div data-settings-section="gyro" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('GYRO AIM')}

          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
            <span style="font-size: 11px; color: #888; letter-spacing: 1px;">Enable</span>
            <div id="settings-gyro-enabled-toggle" style="
              padding: 4px 14px; font-size: 12px;
              background: rgba(80, 90, 60, 0.5);
              border: 1px solid #4a5a3a;
              border-radius: 3px; cursor: pointer;
              color: #c8c090; letter-spacing: 1px;
              transition: all 0.15s;
            ">${settings.gyroEnabled ? 'ON' : 'OFF'}</div>
          </div>

          <div style="margin-bottom: 12px; margin-top: 12px;">
            ${this.sliderHeader('Sensitivity', 'settings-gyro-sens-val', settings.gyroSensitivity.toFixed(3), '11px')}
            <input id="settings-gyro-sens-slider" type="range" min="0.001" max="0.05" step="0.001"
              value="${settings.gyroSensitivity}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Dead Zone (°/s)', 'settings-gyro-dz-val', settings.gyroDeadZone.toFixed(1), '11px')}
            <input id="settings-gyro-dz-slider" type="range" min="0" max="10" step="0.1"
              value="${settings.gyroDeadZone}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
            <span style="font-size: 11px; color: #888; letter-spacing: 1px;">Use Accelerometer</span>
            <div id="settings-gyro-accel-toggle" style="
              padding: 4px 14px; font-size: 12px;
              background: rgba(80, 90, 60, 0.5);
              border: 1px solid #4a5a3a;
              border-radius: 3px; cursor: pointer;
              color: #c8c090; letter-spacing: 1px;
              transition: all 0.15s;
            ">${settings.gyroUseAccelerometer ? 'ON' : 'OFF'}</div>
          </div>

          <div style="display: flex; justify-content: flex-end; margin-top: 12px;">
            <button id="settings-gyro-calibrate-btn" type="button" style="
              padding: 6px 18px; font-size: 12px;
              background: rgba(80, 90, 60, 0.5);
              border: 1px solid #4a5a3a;
              border-radius: 3px; cursor: pointer;
              color: #c8c090; letter-spacing: 1px;
              font-family: 'Courier New', monospace;
              transition: all 0.15s;
            ">CALIBRATE</button>
          </div>
        </div>

        <!-- ═══════ HUD (MOB-W5 / F-MOB-M5) ═══════ -->
        <div data-settings-section="hud" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('HUD')}
          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px; gap: 8px;">
            <span style="font-size: 11px; color: #888; letter-spacing: 1px;">Layout Editor</span>
            <button id="settings-hud-edit-btn" type="button" style="
              padding: 6px 18px; font-size: 12px;
              background: rgba(80, 90, 60, 0.5);
              border: 1px solid #4a5a3a;
              border-radius: 3px; cursor: pointer;
              color: #c8c090; letter-spacing: 1px;
              font-family: 'Courier New', monospace;
              transition: all 0.15s;
            ">EDIT</button>
          </div>
          <div style="font-size: 10px; color: #555; margin-top: 6px;">
            Drag buttons to reposition. Tap the ON/OFF dot to hide a button.
          </div>
        </div>

        <!-- ═══════ Crosshair ═══════ -->
        <div data-settings-section="crosshair" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('CROSSHAIR')}

          <!-- Preview -->
          <div style="text-align: center; margin: 12px 0;">
            <canvas id="crosshair-preview" width="120" height="120"
              style="background: rgba(40,40,30,0.8); border: 1px solid #3a3a2a; border-radius: 3px;"></canvas>
          </div>

          <!-- Style selector -->
          <div style="margin-bottom: 12px;">
            <div style="font-size: 11px; color: #777; margin-bottom: 6px;">Style</div>
            <div id="settings-ch-style-group" style="display: flex; gap: 6px;">
              ${(['classic', 'dot', 'circle'] as const)
                .map(
                  (s) => `<div class="ch-style-btn" data-style="${s}" style="
                    flex: 1; padding: 6px 0; text-align: center;
                    font-size: 11px; font-weight: bold; letter-spacing: 1px;
                    cursor: pointer; border-radius: 3px; transition: all 0.15s;
                    text-transform: uppercase;
                  ">${s}</div>`,
                )
                .join('')}
            </div>
          </div>

          <!-- Color presets -->
          <div style="margin-bottom: 12px;">
            <div style="font-size: 11px; color: #777; margin-bottom: 6px;">Color</div>
            <div id="settings-ch-colors" style="display: flex; gap: 6px; flex-wrap: wrap;">
              ${COLOR_PRESETS.map(
                (c) => `<div class="ch-color-btn" data-color="${c.value}" title="${c.label}" style="
                  width: 28px; height: 28px; border-radius: 4px; cursor: pointer;
                  background: ${c.value}; border: 2px solid transparent;
                  transition: border-color 0.15s;
                "></div>`,
              ).join('')}
              <input id="settings-ch-color-custom" type="color" value="${settings.crosshair.color}"
                style="width: 28px; height: 28px; border: none; padding: 0; cursor: pointer;
                border-radius: 4px; background: transparent;" title="Custom color" />
            </div>
          </div>

          <!-- Sliders -->
          <div style="margin-bottom: 10px;">
            ${this.sliderHeader('Size', 'settings-ch-size-val', String(settings.crosshair.size) + 'px', '11px')}
            <input id="settings-ch-size-slider" type="range" min="2" max="20" step="1"
              value="${settings.crosshair.size}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 10px;">
            ${this.sliderHeader('Gap', 'settings-ch-gap-val', String(settings.crosshair.gap) + 'px', '11px')}
            <input id="settings-ch-gap-slider" type="range" min="0" max="10" step="1"
              value="${settings.crosshair.gap}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 10px;">
            ${this.sliderHeader('Thickness', 'settings-ch-thick-val', String(settings.crosshair.thickness) + 'px', '11px')}
            <input id="settings-ch-thick-slider" type="range" min="1" max="5" step="1"
              value="${settings.crosshair.thickness}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 10px;">
            ${this.sliderHeader('Opacity', 'settings-ch-alpha-val', Math.round(settings.crosshair.alpha * 100) + '%', '11px')}
            <input id="settings-ch-alpha-slider" type="range" min="0.1" max="1" step="0.05"
              value="${settings.crosshair.alpha}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <!-- Toggles -->
          <div style="display: flex; flex-wrap: wrap; gap: 10px; margin-top: 8px;">
            ${this.toggleHtml('settings-ch-outline', 'Outline', settings.crosshair.outline)}
            ${this.toggleHtml('settings-ch-dot', 'Center Dot', settings.crosshair.dot)}
            ${this.toggleHtml('settings-ch-dynamic', 'Dynamic', settings.crosshair.dynamic)}
          </div>
        </div>

        <!-- ═══════ FOV ═══════ -->
        <div data-settings-section="fov" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('FIELD OF VIEW')}
          ${this.sliderHeader('FOV', 'settings-fov-val', String(this.extendedSettings.fov), '11px')}
          <input id="settings-fov-slider" type="range" min="${FOV_MIN}" max="${FOV_MAX}" step="${FOV_STEP}"
            value="${this.extendedSettings.fov}"
            style="width: 100%; accent-color: #e94560; cursor: pointer;" />
        </div>

        <!-- ═══════ Resolution / Display ═══════ -->
        <div data-settings-section="display" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('DISPLAY')}
          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
            <span style="font-size: 11px; color: #888; letter-spacing: 1px;">Fullscreen</span>
            <div id="settings-fullscreen-btn" style="
              padding: 6px 18px; font-size: 12px;
              background: rgba(60,60,50,0.4); border: 1px solid #3a3a2a;
              border-radius: 3px; cursor: pointer;
              color: #c8c090; letter-spacing: 1px; transition: all 0.15s;
            ">TOGGLE</div>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 12px;">
            <span style="font-size: 11px; color: #888; letter-spacing: 1px;">Theme</span>
            <select id="settings-theme-select" style="
              background: rgba(40,40,30,0.9); color: #c8c090; border: 1px solid #3a3a2a;
              border-radius: 3px; padding: 6px 10px; font-size: 12px;
              font-family: 'Courier New', monospace; cursor: pointer;
              outline: none;
            ">
              <option value="light" ${settings.theme === 'light' ? 'selected' : ''}>Light</option>
              <option value="dark" ${settings.theme === 'dark' ? 'selected' : ''}>Dark</option>
              <option value="auto" ${settings.theme === 'auto' ? 'selected' : ''}>Auto</option>
            </select>
          </div>
          <div id="settings-viewport-info" style="font-size: 10px; color: #555; margin-top: 6px; text-align: center;">
            ${typeof window !== 'undefined' ? window.innerWidth + ' x ' + window.innerHeight : ''}
          </div>
        </div>

        <!-- ═══════ Shadow Quality ═══════ -->
        <div data-settings-section="shadows" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('SHADOW QUALITY')}
          <div id="settings-shadow-group" style="display: flex; gap: 6px; margin-top: 8px;">
            ${SHADOW_QUALITY_OPTIONS.map(
              (o) => `<div class="shadow-btn" data-shadow="${o.value}" style="
                flex: 1; padding: 8px 0; text-align: center;
                font-size: 12px; font-weight: bold; letter-spacing: 1px;
                cursor: pointer; border-radius: 3px; transition: all 0.15s;
                text-transform: uppercase;
              ">${o.label}</div>`,
            ).join('')}
          </div>
        </div>

        <!-- ═══════ Texture Quality ═══════ -->
        <div data-settings-section="textures" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('TEXTURE QUALITY')}
          <div id="settings-texture-group" style="display: flex; gap: 6px; margin-top: 8px;">
            ${TEXTURE_QUALITY_OPTIONS.map(
              (o) => `<div class="texture-btn" data-texture="${o.value}" style="
                flex: 1; padding: 8px 0; text-align: center;
                font-size: 12px; font-weight: bold; letter-spacing: 1px;
                cursor: pointer; border-radius: 3px; transition: all 0.15s;
                text-transform: uppercase;
              ">${o.label}</div>`,
            ).join('')}
          </div>
        </div>

        <!-- ═══════ Minimap ═══════ -->
        <div data-settings-section="minimap" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          ${this.sectionLabel('MINIMAP')}

          <div style="margin-bottom: 12px; margin-top: 8px;">
            ${this.sliderHeader('Opacity', 'settings-minimap-opacity-val', this.extendedSettings.minimapOpacity.toFixed(1), '11px')}
            <input id="settings-minimap-opacity-slider" type="range"
              min="${MINIMAP_OPACITY_MIN}" max="${MINIMAP_OPACITY_MAX}" step="0.1"
              value="${this.extendedSettings.minimapOpacity}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <div style="margin-bottom: 12px;">
            ${this.sliderHeader('Scale', 'settings-minimap-scale-val', this.extendedSettings.minimapScale.toFixed(1), '11px')}
            <input id="settings-minimap-scale-slider" type="range"
              min="${MINIMAP_SCALE_MIN}" max="${MINIMAP_SCALE_MAX}" step="0.1"
              value="${this.extendedSettings.minimapScale}"
              style="width: 100%; accent-color: #5a6a3a; cursor: pointer;" />
          </div>

          <div style="margin-top: 8px;">
            ${this.toggleHtml('settings-minimap-teammates', 'Show Teammates', this.extendedSettings.minimapShowTeammates)}
          </div>
        </div>

        <!-- ═══════ Controls / Keybinds ═══════ -->
        <!-- BUG-P-12: on mobile the keybind editor is unusable (key-capture
             requires a physical keyboard). Replace the editor with a short
             notice that points the user to the HUD layout editor. -->
        <div data-settings-section="controls" style="border-top: 1px solid #2a2a2a; padding-top: 16px; margin-top: 16px;">
          <div style="font-size: 12px; color: #888; letter-spacing: 2px; margin-bottom: 8px; text-transform: uppercase;">
            CONTROLS
          </div>
          ${
            mobile
              ? `<div style="font-size: 11px; color: #888; line-height: 1.5; padding: 8px 0;">
                  Keybinds are only available on desktop. Use HUD layout editor for touch buttons.
                </div>`
              : `<div id="settings-keybind-container"></div>`
          }
        </div>

        <!-- ═══════ Footer: Reset / Export / Import ═══════ -->
        <div style="
          display: flex; flex-wrap: wrap; gap: 8px;
          margin-top: 20px; padding-top: 14px;
          border-top: 1px solid #2a2a2a;
        ">
          <button id="settings-reset-btn" type="button" style="
            flex: 1 1 auto; min-width: 140px;
            padding: 8px 14px; font-size: 12px;
            background: rgba(120, 40, 40, 0.45);
            border: 1px solid #6a2a2a; border-radius: 3px;
            color: #e08080; cursor: pointer;
            font-family: 'Courier New', monospace; letter-spacing: 1px;
            text-transform: uppercase; transition: all 0.15s;
          ">Reset to Defaults</button>
          <button id="settings-export-btn" type="button" style="
            flex: 1 1 auto; min-width: 100px;
            padding: 8px 14px; font-size: 12px;
            background: rgba(60,60,50,0.5);
            border: 1px solid #3a3a2a; border-radius: 3px;
            color: #c8c090; cursor: pointer;
            font-family: 'Courier New', monospace; letter-spacing: 1px;
            text-transform: uppercase; transition: all 0.15s;
          ">Export</button>
          <button id="settings-import-btn" type="button" style="
            flex: 1 1 auto; min-width: 100px;
            padding: 8px 14px; font-size: 12px;
            background: rgba(60,60,50,0.5);
            border: 1px solid #3a3a2a; border-radius: 3px;
            color: #c8c090; cursor: pointer;
            font-family: 'Courier New', monospace; letter-spacing: 1px;
            text-transform: uppercase; transition: all 0.15s;
          ">Import</button>
          <input id="settings-import-file" type="file" accept="application/json,.json"
            style="display: none;" />
        </div>
        <div id="settings-footer-toast" role="status" aria-live="polite" style="
          display: none; margin-top: 10px; padding: 6px 10px;
          background: rgba(40,40,30,0.9); border: 1px solid #3a3a2a;
          border-radius: 3px; color: #c8c090;
          font-size: 11px; text-align: center; letter-spacing: 1px;
        "></div>

        <!-- Close hint -->
        <div style="
          text-align: center; margin-top: 20px; padding-top: 14px;
          border-top: 1px solid #2a2a2a;
          font-size: 11px; color: #555; letter-spacing: 1px;
        ">
          Press <span style="color: #c8c090;">ESC</span> to close
        </div>
      </div>
    `;

    // Cache element references
    this.fppBtn = this.overlay.querySelector('#settings-fpp-btn')!;
    this.tppBtn = this.overlay.querySelector('#settings-tpp-btn')!;
    this.sensitivitySlider = this.overlay.querySelector('#settings-sens-slider')! as HTMLInputElement;
    this.sensitivityValue = this.overlay.querySelector('#settings-sens-val')!;
    this.distanceSlider = this.overlay.querySelector('#settings-dist-slider')! as HTMLInputElement;
    this.distanceValue = this.overlay.querySelector('#settings-dist-val')!;
    this.heightSlider = this.overlay.querySelector('#settings-height-slider')! as HTMLInputElement;
    this.heightValue = this.overlay.querySelector('#settings-height-val')!;
    this.shoulderToggle = this.overlay.querySelector('#settings-shoulder-toggle')!;

    // Audio
    this.masterVolumeSlider = this.overlay.querySelector('#settings-master-vol-slider')! as HTMLInputElement;
    this.masterVolumeValue = this.overlay.querySelector('#settings-master-vol-val')!;
    this.sfxVolumeSlider = this.overlay.querySelector('#settings-sfx-vol-slider')! as HTMLInputElement;
    this.sfxVolumeValue = this.overlay.querySelector('#settings-sfx-vol-val')!;
    this.musicVolumeSlider = this.overlay.querySelector('#settings-music-vol-slider')! as HTMLInputElement;
    this.musicVolumeValue = this.overlay.querySelector('#settings-music-vol-val')!;
    this.voiceVolumeSlider = this.overlay.querySelector('#settings-voice-vol-slider')! as HTMLInputElement;
    this.voiceVolumeValue = this.overlay.querySelector('#settings-voice-vol-val')!;
    this.announcerVolumeSlider = this.overlay.querySelector('#settings-announcer-vol-slider')! as HTMLInputElement;
    this.announcerVolumeValue = this.overlay.querySelector('#settings-announcer-vol-val')!;
    this.footstepVolumeSlider = this.overlay.querySelector('#settings-footstep-vol-slider')! as HTMLInputElement;
    this.footstepVolumeValue = this.overlay.querySelector('#settings-footstep-vol-val')!;

    // BUG-P-16: mic device picker (optional — markup may be absent in stripped builds)
    this.micDeviceSelect = this.overlay.querySelector('#settings-mic-device-select') as HTMLSelectElement | null;
    this.micPermissionNote = this.overlay.querySelector('#settings-mic-permission-note') as HTMLElement | null;
    this.micRetryBtn = this.overlay.querySelector('#settings-mic-retry-btn') as HTMLButtonElement | null;

    // Graphics quality buttons
    this.qualityBtns = Array.from(this.overlay.querySelectorAll('.quality-btn'));

    // Crosshair preview canvas
    this.crosshairPreviewCanvas = this.overlay.querySelector('#crosshair-preview')! as HTMLCanvasElement;

    // Accessibility
    this.colorblindSelect = this.overlay.querySelector('#settings-colorblind-select')! as HTMLSelectElement;

    // C9 — adsSnap toggle + controlMode select. Optional querySelectors so
    // older `index.html` snapshots without these blocks don't crash.
    this.adsSnapToggle = this.overlay.querySelector('#settings-ads-snap-toggle') as HTMLElement | null;
    this.controlModeSelect = this.overlay.querySelector('#settings-control-mode-select') as HTMLSelectElement | null;

    // Keybind editor — reuse existing instance if buildUI() is called more than once
    // (avoids leaking the prior editor's DOM and listeners). BUG-P-12: skipped
    // on mobile (no container is rendered there).
    const keybindContainer = this.overlay.querySelector('#settings-keybind-container');
    if (keybindContainer) {
      if (!this.keybindEditor) {
        this.keybindEditor = new KeybindEditor();
      }
      keybindContainer.appendChild(this.keybindEditor.getElement());
    }

    // BUG-P-17: close button on mobile.
    const closeBtn = this.overlay.querySelector('#settings-menu-close-btn') as HTMLElement | null;
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hide());
    }

    // Extended settings elements
    this.fovSlider = this.overlay.querySelector('#settings-fov-slider')! as HTMLInputElement;
    this.fovValue = this.overlay.querySelector('#settings-fov-val')!;
    this.shadowBtns = Array.from(this.overlay.querySelectorAll('.shadow-btn'));
    this.textureBtns = Array.from(this.overlay.querySelectorAll('.texture-btn'));
    this.minimapOpacitySlider = this.overlay.querySelector('#settings-minimap-opacity-slider')! as HTMLInputElement;
    this.minimapOpacityValue = this.overlay.querySelector('#settings-minimap-opacity-val')!;
    this.minimapScaleSlider = this.overlay.querySelector('#settings-minimap-scale-slider')! as HTMLInputElement;
    this.minimapScaleValue = this.overlay.querySelector('#settings-minimap-scale-val')!;
    this.minimapTeammatesCheckbox = this.overlay.querySelector('#settings-minimap-teammates')! as HTMLInputElement;

    this.updateModeButtons(settings.cameraMode);
    this.updateQualityButtons(settings.graphicsQuality);
    this.updateShadowButtons(this.extendedSettings.shadowQuality);
    this.updateTextureButtons(this.extendedSettings.textureQuality);
    this.updateStyleButtons(settings.crosshair.style);
    this.updateColorSelection(settings.crosshair.color);
    this.renderCrosshairPreview();
    this.bindEvents();
  }

  // ---------------------------------------------------------------------------
  // Scoped styles (one-shot)
  // ---------------------------------------------------------------------------

  /** Toggle the body theme class so future CSS-var-driven UI picks up the
   *  user's choice. Removes any prior theme-* class first so switches are
   *  idempotent. */
  private static applyThemeClass(theme: UiTheme): void {
    if (typeof document === 'undefined' || !document.body) return;
    document.body.classList.remove('theme-light', 'theme-dark', 'theme-auto');
    document.body.classList.add(`theme-${theme}`);
  }

  private static injectScopedStyles(): void {
    if (document.getElementById('settings-menu-scoped-styles')) return;
    const style = document.createElement('style');
    style.id = 'settings-menu-scoped-styles';
    style.textContent = `
      /* Color theme variables — consumed by future CSS-var-driven UI.
       * Existing inline styles still win; this just wires the choice so a
       * later refactor can read var(--ui-primary) etc. */
      body.theme-dark {
        --ui-primary: #e94560;
        --ui-bg: rgba(20, 24, 20, 0.95);
        --ui-text: #c8c090;
      }
      body.theme-light {
        --ui-primary: #b03050;
        --ui-bg: rgba(240, 238, 228, 0.95);
        --ui-text: #202020;
      }
      body.theme-auto {
        --ui-primary: #e94560;
        --ui-bg: rgba(20, 24, 20, 0.95);
        --ui-text: #c8c090;
      }
      @media (prefers-color-scheme: light) {
        body.theme-auto {
          --ui-primary: #b03050;
          --ui-bg: rgba(240, 238, 228, 0.95);
          --ui-text: #202020;
        }
      }
      #settings-menu input[type="range"] {
        touch-action: pan-x;
      }
      #settings-menu-close-btn:hover {
        background: rgba(233, 69, 96, 0.3);
        color: #fff;
      }
      #settings-section-tabs {
        position: sticky;
        top: 0;
        z-index: 2;
        display: flex;
        gap: 6px;
        overflow-x: auto;
        overscroll-behavior-x: contain;
        padding: 8px 0 10px;
        margin: 0 0 16px;
        background: rgba(20, 24, 20, 0.98);
        border-bottom: 1px solid #2a2a2a;
        scrollbar-width: thin;
      }
      #settings-section-tabs::-webkit-scrollbar {
        height: 4px;
      }
      #settings-section-tabs::-webkit-scrollbar-thumb {
        background: rgba(200, 192, 144, 0.35);
        border-radius: 4px;
      }
      .settings-section-tab {
        flex: 0 0 auto;
        min-height: 36px;
        padding: 8px 12px;
        background: rgba(60,60,50,0.45);
        border: 1px solid #3a3a2a;
        border-radius: 3px;
        color: #c8c090;
        cursor: pointer;
        font-family: "Courier New", monospace;
        font-size: 11px;
        letter-spacing: 1px;
        text-transform: uppercase;
        white-space: nowrap;
      }
      .settings-section-tab:hover {
        background: rgba(233,69,96,0.2);
        color: #fff;
        border-color: rgba(233,69,96,0.45);
      }
    `;
    document.head.appendChild(style);
  }

  // ---------------------------------------------------------------------------
  // HTML helpers
  // ---------------------------------------------------------------------------

  private sectionLabel(text: string, fontSize = '12px', color = '#888'): string {
    return `<div style="font-size: ${fontSize}; color: ${color}; letter-spacing: 2px; margin-bottom: 8px; text-transform: uppercase;">
      ${text}
    </div>`;
  }

  private sliderHeader(label: string, valId: string, valText: string, fontSize = '12px'): string {
    return `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
      <span style="font-size: ${fontSize}; color: #888; letter-spacing: 2px; text-transform: uppercase;">${label}</span>
      <span id="${valId}" style="font-size: 13px; color: #c8c090;">${valText}</span>
    </div>`;
  }

  private toggleHtml(id: string, label: string, checked: boolean): string {
    return `<label style="display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 11px; color: #aaa;">
      <input type="checkbox" id="${id}" ${checked ? 'checked' : ''}
        style="accent-color: #5a6a3a; cursor: pointer;" />
      ${label}
    </label>`;
  }

  private settingsTabStripHtml(): string {
    const tabs = SETTINGS_NAV_SECTIONS.map((section) => `
      <button type="button" class="settings-section-tab" data-settings-tab="${section.id}">
        ${section.label}
      </button>
    `).join('');
    return `
      <div id="settings-section-tabs" aria-label="Settings sections">
        ${tabs}
      </div>
    `;
  }

  /** MOB-W6: render a single quick-buy slot (label + dropdown). Selected
   *  weapon comes from the registered loadout getter; defaults to the first
   *  option when no getter is wired (desktop / first-time render). */
  private renderQuickBuySlot(slot: 0 | 1 | 2): string {
    const current = (this.getQuickBuyLoadout?.() ?? [])[slot] ?? '';
    const opts = QUICK_BUY_WEAPON_OPTIONS.map(
      (o) => `<option value="${o.id}"${o.id === current ? ' selected' : ''}>${o.label}</option>`,
    ).join('');
    return `
      <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 8px;">
        <span style="font-size: 11px; color: #888; letter-spacing: 1px; min-width: 60px;">SLOT ${slot + 1}</span>
        <select id="settings-quickbuy-${slot}" style="
          flex: 1; padding: 6px 10px; font-size: 12px;
          background: rgba(40,40,30,0.8); border: 1px solid #3a3a2a;
          border-radius: 3px; color: #c8c090; cursor: pointer;
          font-family: 'Courier New', monospace;
        ">${opts}</select>
      </div>`;
  }

  // ---------------------------------------------------------------------------
  // Event binding
  // ---------------------------------------------------------------------------

  private bindEvents(): void {
    this.bindSectionTabs();

    // ── Camera mode ──
    this.fppBtn.addEventListener('click', () => {
      settingsStore.update({ cameraMode: 'fpp' });
      this.updateModeButtons('fpp');
      this.updateTPPGroupState('fpp');
    });

    this.tppBtn.addEventListener('click', () => {
      settingsStore.update({ cameraMode: 'tpp' });
      this.updateModeButtons('tpp');
      this.updateTPPGroupState('tpp');
    });

    // ── Sensitivity ──
    this.sensitivitySlider.addEventListener('input', () => {
      const val = parseFloat(this.sensitivitySlider.value);
      this.sensitivityValue.textContent = val.toFixed(4);
      settingsStore.update({ sensitivity: val });
    });

    // ── TPP sliders ──
    this.distanceSlider.addEventListener('input', () => {
      const val = parseFloat(this.distanceSlider.value);
      this.distanceValue.textContent = val.toFixed(1) + 'm';
      settingsStore.update({ tppDistance: val });
    });

    this.heightSlider.addEventListener('input', () => {
      const val = parseFloat(this.heightSlider.value);
      this.heightValue.textContent = val.toFixed(1) + 'm';
      settingsStore.update({ tppHeight: val });
    });

    this.shoulderToggle.addEventListener('click', () => {
      const current = settingsStore.get().tppShoulder;
      const next = current >= 0 ? -0.5 : 0.5;
      settingsStore.update({ tppShoulder: next });
      this.shoulderToggle.textContent = next >= 0 ? 'RIGHT' : 'LEFT';
    });

    // ── Graphics quality ──
    for (const btn of this.qualityBtns) {
      btn.addEventListener('click', () => {
        const quality = btn.getAttribute('data-quality') as GraphicsQuality;
        settingsStore.update({ graphicsQuality: quality });
        this.updateQualityButtons(quality);
      });
    }

    // ── Audio ──
    this.masterVolumeSlider.addEventListener('input', () => {
      const val = parseFloat(this.masterVolumeSlider.value);
      this.masterVolumeValue.textContent = Math.round(val * 100) + '%';
      settingsStore.update({ masterVolume: val });
      audioManager.setMasterVolume(val);
    });

    this.sfxVolumeSlider.addEventListener('input', () => {
      const val = parseFloat(this.sfxVolumeSlider.value);
      this.sfxVolumeValue.textContent = Math.round(val * 100) + '%';
      settingsStore.update({ sfxVolume: val });
      audioManager.setSfxVolume(val);
    });

    this.musicVolumeSlider.addEventListener('input', () => {
      const val = parseFloat(this.musicVolumeSlider.value);
      this.musicVolumeValue.textContent = Math.round(val * 100) + '%';
      settingsStore.update({ musicVolume: val });
      musicSystem.setVolume(val);
    });

    this.voiceVolumeSlider.addEventListener('input', () => {
      const val = parseFloat(this.voiceVolumeSlider.value);
      this.voiceVolumeValue.textContent = Math.round(val * 100) + '%';
      settingsStore.update({ voiceVolume: val });
      // F-AV-004: propagate to live VoiceChatManager so already-attached peer
      // <audio> elements pick up the new volume immediately.
      voiceChat.setVoiceVolume(val);
    });

    this.announcerVolumeSlider.addEventListener('input', () => {
      const val = parseFloat(this.announcerVolumeSlider.value);
      this.announcerVolumeValue.textContent = Math.round(val * 100) + '%';
      settingsStore.update({ announcerVolume: val });
      audioManager.setAnnouncerVolume(val);
      announcer.setVolume(val);
    });

    this.footstepVolumeSlider.addEventListener('input', () => {
      const val = parseFloat(this.footstepVolumeSlider.value);
      this.footstepVolumeValue.textContent = Math.round(val * 100) + '%';
      settingsStore.update({ footstepVolume: val });
      audioManager.setFootstepVolume(val);
    });

    // BUG-P-16: Microphone Input device picker
    if (this.micDeviceSelect) {
      this.micDeviceSelect.addEventListener('change', () => {
        const deviceId = this.micDeviceSelect!.value;
        settingsStore.update({ micDeviceId: deviceId });
        // TODO: voiceChat.acquireMicrophone() does not yet accept a deviceId.
        // When that signature lands, propagate the change to the live track via
        //   voiceChat.setMicDevice(deviceId)
        // so the user does not need to re-join voice for the new mic to apply.
      });
      // Populate now and again when device list changes (e.g. user plugs in a headset).
      void this.populateMicDevices();
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
        navigator.mediaDevices.addEventListener('devicechange', () => {
          void this.populateMicDevices();
        });
      }
    }
    if (this.micRetryBtn) {
      this.micRetryBtn.addEventListener('click', async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          // Permission granted — release the probe stream and re-enumerate so
          // the dropdown gets real labels.
          for (const t of stream.getTracks()) t.stop();
        } catch {
          // Still denied — populateMicDevices() will re-show the inline note.
        }
        await this.populateMicDevices();
      });
    }

    // ── Colorblind mode ──
    this.colorblindSelect.addEventListener('change', () => {
      const mode = this.colorblindSelect.value as ColorblindMode;
      settingsStore.update({ colorblindMode: mode });
      applyColorblindCSS(mode);
    });

    // C9 — ADS Snap toggle + Control Mode dropdown
    this.adsSnapToggle?.addEventListener('click', () => {
      const next = !settingsStore.get().adsSnapEnabled;
      settingsStore.update({ adsSnapEnabled: next });
      if (this.adsSnapToggle) this.adsSnapToggle.textContent = next ? 'ON' : 'OFF';
    });
    this.controlModeSelect?.addEventListener('change', () => {
      const value = this.controlModeSelect!.value as 'simple' | 'advanced';
      settingsStore.update({ controlMode: value });
    });

    // ── Crosshair: style buttons ──
    const styleBtns = this.overlay.querySelectorAll<HTMLElement>('.ch-style-btn');
    for (const btn of styleBtns) {
      btn.addEventListener('click', () => {
        const style = btn.getAttribute('data-style') as CrosshairConfig['style'];
        this.updateCrosshair({ style });
        this.updateStyleButtons(style);
      });
    }

    // ── Crosshair: color presets ──
    const colorBtns = this.overlay.querySelectorAll<HTMLElement>('.ch-color-btn');
    for (const btn of colorBtns) {
      btn.addEventListener('click', () => {
        const color = btn.getAttribute('data-color')!;
        this.updateCrosshair({ color });
        this.updateColorSelection(color);
        // Sync custom picker
        const picker = this.overlay.querySelector('#settings-ch-color-custom') as HTMLInputElement;
        if (picker) picker.value = color;
      });
    }

    // Custom color picker
    const customColor = this.overlay.querySelector('#settings-ch-color-custom') as HTMLInputElement;
    if (customColor) {
      customColor.addEventListener('input', () => {
        this.updateCrosshair({ color: customColor.value });
        this.updateColorSelection(customColor.value);
      });
    }

    // ── Crosshair: sliders ──
    this.bindCrosshairSlider('settings-ch-size-slider', 'settings-ch-size-val', 'size', 'px', true);
    this.bindCrosshairSlider('settings-ch-gap-slider', 'settings-ch-gap-val', 'gap', 'px', true);
    this.bindCrosshairSlider('settings-ch-thick-slider', 'settings-ch-thick-val', 'thickness', 'px', true);

    // Alpha slider uses percentage display, not px suffix
    const alphaSlider = this.overlay.querySelector('#settings-ch-alpha-slider') as HTMLInputElement;
    const alphaVal = this.overlay.querySelector('#settings-ch-alpha-val') as HTMLElement;
    if (alphaSlider && alphaVal) {
      alphaSlider.addEventListener('input', () => {
        const val = parseFloat(alphaSlider.value);
        alphaVal.textContent = Math.round(val * 100) + '%';
        this.updateCrosshair({ alpha: val } as Partial<CrosshairConfig>);
      });
    }

    // ── Crosshair: toggles ──
    this.bindCrosshairToggle('settings-ch-outline', 'outline');
    this.bindCrosshairToggle('settings-ch-dot', 'dot');
    this.bindCrosshairToggle('settings-ch-dynamic', 'dynamic');

    // ── FOV slider ──
    this.fovSlider.addEventListener('input', () => {
      const val = clampFov(parseInt(this.fovSlider.value, 10));
      this.fovValue.textContent = String(val);
      this.extendedSettings.fov = val;
      saveExtendedSettings(this.extendedSettings);
      // Notify game to apply FOV to camera
      window.dispatchEvent(new CustomEvent('settings-fov-change', { detail: { fov: val } }));
    });

    // ── Fullscreen toggle ──
    const fullscreenBtn = this.overlay.querySelector('#settings-fullscreen-btn');
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', () => {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch(() => {});
        } else {
          document.exitFullscreen().catch(() => {});
        }
      });
    }

    // ── Shadow quality ──
    for (const btn of this.shadowBtns) {
      btn.addEventListener('click', () => {
        const quality = btn.getAttribute('data-shadow') as ShadowQuality;
        this.extendedSettings.shadowQuality = quality;
        saveExtendedSettings(this.extendedSettings);
        this.updateShadowButtons(quality);
      });
    }

    // ── Texture quality ──
    for (const btn of this.textureBtns) {
      btn.addEventListener('click', () => {
        const quality = btn.getAttribute('data-texture') as TextureQuality;
        this.extendedSettings.textureQuality = quality;
        saveExtendedSettings(this.extendedSettings);
        this.updateTextureButtons(quality);
      });
    }

    // ── Minimap opacity ──
    this.minimapOpacitySlider.addEventListener('input', () => {
      const val = clampMinimapOpacity(parseFloat(this.minimapOpacitySlider.value));
      this.minimapOpacityValue.textContent = val.toFixed(1);
      this.extendedSettings.minimapOpacity = val;
      saveExtendedSettings(this.extendedSettings);
    });

    // ── Minimap scale ──
    this.minimapScaleSlider.addEventListener('input', () => {
      const val = clampMinimapScale(parseFloat(this.minimapScaleSlider.value));
      this.minimapScaleValue.textContent = val.toFixed(1);
      this.extendedSettings.minimapScale = val;
      saveExtendedSettings(this.extendedSettings);
    });

    // ── Minimap show teammates ──
    this.minimapTeammatesCheckbox.addEventListener('change', () => {
      this.extendedSettings.minimapShowTeammates = this.minimapTeammatesCheckbox.checked;
      saveExtendedSettings(this.extendedSettings);
    });

    // ── UI hover sounds on prominent buttons ──
    const hoverButtons = [
      this.fppBtn,
      this.tppBtn,
      ...this.qualityBtns,
      ...this.shadowBtns,
      ...this.textureBtns,
    ];
    for (const btn of hoverButtons) {
      if (btn) btn.addEventListener('mouseenter', () => playUIHover());
    }

    this.bindMobileTouchAndGyroSections();
    this.bindFooterAndSearch();
  }

  /** Wire the search input (filters visible sections by label), the theme
   *  dropdown (toggles body theme-* class + persists), and the footer
   *  Reset/Export/Import buttons. */
  private bindFooterAndSearch(): void {
    // ── Settings search ──
    const searchInput = this.overlay.querySelector('#settings-search-input') as HTMLInputElement | null;
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        this.applySectionSearchFilter(searchInput.value);
      });
    }

    // ── Theme dropdown ──
    const themeSelect = this.overlay.querySelector('#settings-theme-select') as HTMLSelectElement | null;
    if (themeSelect) {
      themeSelect.addEventListener('change', () => {
        const theme = themeSelect.value as UiTheme;
        settingsStore.update({ theme });
        SettingsMenu.applyThemeClass(theme);
      });
    }

    // ── Reset to defaults ──
    // Uses an in-DOM confirm modal (matches the dark-tactical card styling
    // used elsewhere). Replaces the OS-native window.confirm() so the modal
    // is consistent with the rest of the UI and is visually styleable.
    const resetBtn = this.overlay.querySelector('#settings-reset-btn') as HTMLButtonElement | null;
    resetBtn?.addEventListener('click', () => {
      this.showResetConfirmModal(() => {
        settingsStore.reset();
        // Persist + reset the extended settings bucket too (FOV, shadow, texture, minimap).
        this.extendedSettings = { ...EXTENDED_SETTINGS_DEFAULTS };
        saveExtendedSettings(this.extendedSettings);
        // Re-apply theme class (defaults are 'dark').
        SettingsMenu.applyThemeClass(settingsStore.get().theme);
        // Refresh the UI so all controls reflect the reset values.
        this.syncFromSettings();
        this.showFooterToast('Settings reset');
      });
    });

    // ── Export JSON ──
    const exportBtn = this.overlay.querySelector('#settings-export-btn') as HTMLButtonElement | null;
    exportBtn?.addEventListener('click', () => {
      this.exportSettingsAsJson();
    });

    // ── Import JSON ──
    const importBtn = this.overlay.querySelector('#settings-import-btn') as HTMLButtonElement | null;
    const importFile = this.overlay.querySelector('#settings-import-file') as HTMLInputElement | null;
    importBtn?.addEventListener('click', () => {
      importFile?.click();
    });
    importFile?.addEventListener('change', () => {
      const file = importFile.files?.[0];
      if (!file) return;
      void this.importSettingsFromFile(file).finally(() => {
        // Clear the input so re-selecting the same file fires `change` again.
        importFile.value = '';
      });
    });
  }

  /** Filter visible sections by case-insensitive substring match on the
   *  nav label. Empty query restores all sections. */
  private applySectionSearchFilter(query: string): void {
    const q = query.trim().toLowerCase();
    for (const nav of SETTINGS_NAV_SECTIONS) {
      const section = this.overlay.querySelector<HTMLElement>(`[data-settings-section="${nav.id}"]`);
      if (!section) continue;
      const visible = q.length === 0 || nav.label.toLowerCase().includes(q);
      // Use 'none' rather than visibility so collapsed sections don't leave
      // a gap. The TPP section has inline display logic but only mutates
      // opacity/pointer-events, so a display swap is safe.
      section.style.display = visible ? '' : 'none';
    }
  }

  /** Export the current settings as a downloadable JSON blob. */
  private exportSettingsAsJson(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    try {
      const payload = {
        version: 1,
        settings: settingsStore.get(),
        extended: this.extendedSettings,
      };
      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'counter-pump-settings.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so the download starts before we drop the URL.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      this.showFooterToast('Settings exported');
    } catch {
      this.showFooterToast('Export failed');
    }
  }

  /** Validate + apply a settings JSON file. Validation requires at least one
   *  recognized field on the parsed payload (either at the root or nested
   *  under `.settings`). */
  private async importSettingsFromFile(file: File): Promise<void> {
    const KNOWN_FIELDS: Array<keyof GameSettings> = [
      'cameraMode', 'tppDistance', 'tppHeight', 'tppShoulder', 'sensitivity',
      'selectedSkinId', 'crosshair', 'graphicsQuality', 'masterVolume',
      'sfxVolume', 'musicVolume', 'announcerVolume', 'footstepVolume',
      'touchSensitivity', 'touchJoystickDeadzone', 'gyroEnabled',
      'gyroSensitivity', 'gyroUseAccelerometer', 'gyroDeadZone',
      'touchSplitPercent', 'aimAssistStrength', 'adsSnapEnabled',
      'scopeSensitivity', 'controlMode', 'colorblindMode', 'voiceVolume',
      'voiceEnabled', 'micDeviceId', 'theme',
    ];
    const KNOWN_EXTENDED: Array<keyof ExtendedSettings> = [
      'fov', 'shadowQuality', 'textureQuality',
      'minimapOpacity', 'minimapScale', 'minimapShowTeammates',
    ];

    try {
      const text = await file.text();
      const raw = JSON.parse(text) as unknown;
      if (typeof raw !== 'object' || raw === null) {
        this.showFooterToast('Import failed');
        return;
      }

      const root = raw as Record<string, unknown>;
      // Accept either { settings: {...}, extended: {...} } (matches export) or
      // a flat object whose keys are GameSettings fields.
      const settingsObj = (root.settings && typeof root.settings === 'object')
        ? (root.settings as Record<string, unknown>)
        : root;
      const extendedObj = (root.extended && typeof root.extended === 'object')
        ? (root.extended as Record<string, unknown>)
        : null;

      const settingsUpdate: Partial<GameSettings> = {};
      for (const key of KNOWN_FIELDS) {
        if (key in settingsObj) {
          (settingsUpdate as Record<string, unknown>)[key] = settingsObj[key];
        }
      }
      const extendedUpdate: Partial<ExtendedSettings> = {};
      if (extendedObj) {
        for (const key of KNOWN_EXTENDED) {
          if (key in extendedObj) {
            (extendedUpdate as Record<string, unknown>)[key] = extendedObj[key];
          }
        }
      }

      const recognized = Object.keys(settingsUpdate).length + Object.keys(extendedUpdate).length;
      if (recognized === 0) {
        this.showFooterToast('Import failed');
        return;
      }

      // Apply per-field via update() so the store's clamp + crosshair
      // deep-merge logic runs.
      for (const [k, v] of Object.entries(settingsUpdate)) {
        settingsStore.update({ [k]: v } as Partial<GameSettings>);
      }
      if (Object.keys(extendedUpdate).length > 0) {
        this.extendedSettings = { ...this.extendedSettings, ...extendedUpdate };
        saveExtendedSettings(this.extendedSettings);
      }
      // Theme may have changed — reapply class.
      SettingsMenu.applyThemeClass(settingsStore.get().theme);
      this.syncFromSettings();
      this.showFooterToast('Settings imported');
    } catch {
      this.showFooterToast('Import failed');
    }
  }

  /** Briefly surface a status message under the footer button row. */
  private showFooterToast(message: string): void {
    const toast = this.overlay.querySelector('#settings-footer-toast') as HTMLElement | null;
    if (!toast) return;
    toast.textContent = message;
    toast.style.display = 'block';
    window.setTimeout(() => {
      toast.style.display = 'none';
    }, 2400);
  }

  /** In-DOM "Reset to Defaults" confirmation. Matches the dark-tactical card
   *  styling used by the rest of the settings menu. Backdrop tap dismisses;
   *  Cancel dismisses; Confirm fires `onConfirm` and then dismisses. The
   *  modal is appended to `document.body` (z-index above the settings
   *  overlay's z:70) and torn down on close so it never leaks. */
  private showResetConfirmModal(onConfirm: () => void): void {
    if (typeof document === 'undefined') {
      // Headless / non-DOM env — fall back to immediate confirm.
      onConfirm();
      return;
    }

    const backdrop = document.createElement('div');
    backdrop.id = 'settings-reset-confirm-backdrop';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');
    backdrop.style.cssText =
      'position: fixed; inset: 0; z-index: 80; ' +
      'display: flex; align-items: center; justify-content: center; ' +
      'background: rgba(0, 0, 0, 0.72); ' +
      'font-family: "Courier New", monospace;';

    const card = document.createElement('div');
    card.style.cssText =
      'width: min(380px, calc(100vw - 32px)); ' +
      'padding: 22px 24px; ' +
      'background: rgba(20, 24, 20, 0.98); ' +
      'border: 1px solid #3a3a2a; border-radius: 4px; ' +
      'box-shadow: 0 18px 42px rgba(0, 0, 0, 0.55); ' +
      'color: #ffffff; user-select: none;';

    const title = document.createElement('div');
    title.id = 'settings-reset-confirm-title';
    title.textContent = 'Reset all settings to defaults?';
    title.style.cssText =
      'font-size: 14px; color: #c8c090; ' +
      'letter-spacing: 2px; text-transform: uppercase; ' +
      'margin: 0 0 8px; text-align: center;';

    const subtitle = document.createElement('div');
    subtitle.textContent = 'This cannot be undone.';
    subtitle.style.cssText =
      'font-size: 11px; color: #888; ' +
      'letter-spacing: 1px; text-align: center; ' +
      'margin: 0 0 18px;';

    const actions = document.createElement('div');
    actions.style.cssText =
      'display: flex; gap: 10px; justify-content: flex-end;';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.id = 'settings-reset-confirm-cancel';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.cssText =
      'flex: 1 1 auto; min-width: 100px; ' +
      'padding: 8px 14px; font-size: 12px; ' +
      'background: rgba(60, 60, 50, 0.5); ' +
      'border: 1px solid #3a3a2a; border-radius: 3px; ' +
      "color: #c8c090; cursor: pointer; " +
      "font-family: 'Courier New', monospace; letter-spacing: 1px; " +
      'text-transform: uppercase; transition: all 0.15s;';

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.id = 'settings-reset-confirm-ok';
    confirmBtn.textContent = 'Confirm';
    confirmBtn.style.cssText =
      'flex: 1 1 auto; min-width: 100px; ' +
      'padding: 8px 14px; font-size: 12px; ' +
      'background: rgba(180, 50, 60, 0.55); ' +
      'border: 1px solid #8a2a2a; border-radius: 3px; ' +
      "color: #ffd0d0; cursor: pointer; " +
      "font-family: 'Courier New', monospace; letter-spacing: 1px; " +
      'text-transform: uppercase; transition: all 0.15s;';

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    card.appendChild(title);
    card.appendChild(subtitle);
    card.appendChild(actions);
    backdrop.appendChild(card);
    document.body.appendChild(backdrop);

    backdrop.setAttribute('aria-labelledby', 'settings-reset-confirm-title');

    let disposed = false;
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      document.removeEventListener('keydown', onKey, true);
      if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        e.stopPropagation();
        dispose();
      } else if (e.code === 'Enter') {
        e.stopPropagation();
        dispose();
        onConfirm();
      }
    };

    // Backdrop tap dismisses — but only when the click target IS the backdrop
    // (clicks inside the card must not close).
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) dispose();
    });
    cancelBtn.addEventListener('click', () => dispose());
    confirmBtn.addEventListener('click', () => {
      dispose();
      onConfirm();
    });

    document.addEventListener('keydown', onKey, true);

    // Focus the cancel button by default so an accidental Enter doesn't reset.
    try { cancelBtn.focus(); } catch { /* JSDOM may throw */ }
  }

  private bindSectionTabs(): void {
    const tabs = this.overlay.querySelectorAll<HTMLElement>('[data-settings-tab]');
    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        const section = tab.dataset.settingsTab as SettingsSectionId | undefined;
        if (!section) return;
        const target = this.overlay.querySelector<HTMLElement>(`[data-settings-section="${section}"]`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  /** MOB-W5 / MOB-L2 / F-MOB-M1 / F-MOB-H1 / F-MOB-M6 binding glue. */
  private bindMobileTouchAndGyroSections(): void {
    // Touch joystick deadzone
    const dz = this.overlay.querySelector('#settings-touch-deadzone-slider') as HTMLInputElement | null;
    const dzVal = this.overlay.querySelector('#settings-touch-deadzone-val') as HTMLElement | null;
    dz?.addEventListener('input', () => {
      const v = parseFloat(dz!.value);
      if (dzVal) dzVal.textContent = v.toFixed(2);
      settingsStore.update({ touchJoystickDeadzone: v });
    });

    // Per-scope sensitivity (hipfire / reddot / scope4x)
    const bindScopeSlider = (id: string, valId: string, key: string) => {
      const slider = this.overlay.querySelector(`#${id}`) as HTMLInputElement | null;
      const valEl = this.overlay.querySelector(`#${valId}`) as HTMLElement | null;
      slider?.addEventListener('input', () => {
        const v = parseFloat(slider!.value);
        if (valEl) valEl.textContent = v.toFixed(2);
        const current = { ...settingsStore.get().scopeSensitivity };
        current[key] = v;
        settingsStore.update({ scopeSensitivity: current });
      });
    };
    bindScopeSlider('settings-sens-hip-slider', 'settings-sens-hip-val', 'hipfire');
    bindScopeSlider('settings-sens-reddot-slider', 'settings-sens-reddot-val', 'reddot');
    bindScopeSlider('settings-sens-scoped-slider', 'settings-sens-scoped-val', 'scope4x');

    // Gyro Aim section
    const gyroEnabled = this.overlay.querySelector('#settings-gyro-enabled-toggle') as HTMLElement | null;
    gyroEnabled?.addEventListener('click', async () => {
      const next = !settingsStore.get().gyroEnabled;
      settingsStore.update({ gyroEnabled: next });
      gyroEnabled.textContent = next ? 'ON' : 'OFF';
      // F-MOB-M6: when enabling gyro, request permission. If denied, show banner.
      if (next && this.onRequestGyroPermission) {
        const ok = await this.onRequestGyroPermission();
        if (!ok) {
          showGyroPermissionBanner({
            onRetry: async () => {
              if (!this.onRequestGyroPermission) return false;
              return await this.onRequestGyroPermission();
            },
          });
        }
      }
    });

    const gyroSens = this.overlay.querySelector('#settings-gyro-sens-slider') as HTMLInputElement | null;
    const gyroSensVal = this.overlay.querySelector('#settings-gyro-sens-val') as HTMLElement | null;
    gyroSens?.addEventListener('input', () => {
      const v = parseFloat(gyroSens!.value);
      if (gyroSensVal) gyroSensVal.textContent = v.toFixed(3);
      settingsStore.update({ gyroSensitivity: v });
    });

    const gyroDz = this.overlay.querySelector('#settings-gyro-dz-slider') as HTMLInputElement | null;
    const gyroDzVal = this.overlay.querySelector('#settings-gyro-dz-val') as HTMLElement | null;
    gyroDz?.addEventListener('input', () => {
      const v = parseFloat(gyroDz!.value);
      if (gyroDzVal) gyroDzVal.textContent = v.toFixed(1);
      settingsStore.update({ gyroDeadZone: v });
    });

    const gyroAccel = this.overlay.querySelector('#settings-gyro-accel-toggle') as HTMLElement | null;
    gyroAccel?.addEventListener('click', () => {
      const next = !settingsStore.get().gyroUseAccelerometer;
      settingsStore.update({ gyroUseAccelerometer: next });
      gyroAccel.textContent = next ? 'ON' : 'OFF';
    });

    const calibrateBtn = this.overlay.querySelector('#settings-gyro-calibrate-btn') as HTMLButtonElement | null;
    calibrateBtn?.addEventListener('click', () => {
      this.onCalibrateGyro?.();
    });

    // MOB-W5 / F-MOB-M5: HUD layout editor entrypoint.
    const hudEditBtn = this.overlay.querySelector('#settings-hud-edit-btn') as HTMLButtonElement | null;
    hudEditBtn?.addEventListener('click', () => {
      this.hide();
      this.onOpenHudEditor?.();
    });

    // MOB-W6: quick-buy dropdowns. Each change persists via the host
    // callback (which writes through QuickBuyController.saveLoadout and
    // refreshes the on-screen button labels).
    for (const slot of [0, 1, 2] as const) {
      const select = this.overlay.querySelector(`#settings-quickbuy-${slot}`) as HTMLSelectElement | null;
      if (!select) continue;
      select.addEventListener('change', () => {
        const weaponId = select.value;
        if (!weaponId) return;
        this.onQuickBuySlotChange?.(slot, weaponId);
      });
    }
  }

  private bindCrosshairSlider(
    sliderId: string,
    valId: string,
    key: keyof CrosshairConfig,
    suffix: string,
    isInt: boolean,
  ): void {
    const slider = this.overlay.querySelector(`#${sliderId}`) as HTMLInputElement;
    const valEl = this.overlay.querySelector(`#${valId}`) as HTMLElement;
    if (!slider || !valEl) return;

    slider.addEventListener('input', () => {
      const val = isInt ? parseInt(slider.value, 10) : parseFloat(slider.value);
      valEl.textContent = val + suffix;
      this.updateCrosshair({ [key]: val } as Partial<CrosshairConfig>);
    });
  }

  private bindCrosshairToggle(checkboxId: string, key: keyof CrosshairConfig): void {
    const cb = this.overlay.querySelector(`#${checkboxId}`) as HTMLInputElement;
    if (!cb) return;
    cb.addEventListener('change', () => {
      this.updateCrosshair({ [key]: cb.checked } as Partial<CrosshairConfig>);
    });
  }

  // ---------------------------------------------------------------------------
  // Crosshair helpers
  // ---------------------------------------------------------------------------

  private updateCrosshair(partial: Partial<CrosshairConfig>): void {
    const current = settingsStore.get().crosshair;
    const merged = { ...current, ...partial };
    settingsStore.update({ crosshair: merged });
    this.renderCrosshairPreview();
  }

  private renderCrosshairPreview(): void {
    const canvas = this.crosshairPreviewCanvas;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const config = settingsStore.get().crosshair;
    const data = generateCrosshairRenderData(config, 0);

    const w = canvas.width;
    const h = canvas.height;
    const cx = w / 2;
    const cy = h / 2;

    ctx.clearRect(0, 0, w, h);
    ctx.globalAlpha = data.alpha;

    if (data.style === 'classic' && data.lines) {
      for (const line of data.lines) {
        if (data.outline) {
          const ot = data.outlineThickness;
          ctx.fillStyle = '#000000';
          ctx.fillRect(cx + line.x - ot, cy + line.y - ot, line.width + ot * 2, line.height + ot * 2);
        }
        ctx.fillStyle = data.color;
        ctx.fillRect(cx + line.x, cy + line.y, line.width, line.height);
      }
    }

    if (data.style === 'dot' && data.dot) {
      if (data.outline) {
        ctx.beginPath();
        ctx.arc(cx, cy, data.dot.radius + data.outlineThickness, 0, Math.PI * 2);
        ctx.fillStyle = '#000000';
        ctx.fill();
      }
      ctx.beginPath();
      ctx.arc(cx, cy, data.dot.radius, 0, Math.PI * 2);
      ctx.fillStyle = data.color;
      ctx.fill();
    }

    if (data.style === 'circle' && data.circle) {
      if (data.outline) {
        ctx.beginPath();
        ctx.arc(cx, cy, data.circle.radius, 0, Math.PI * 2);
        ctx.lineWidth = data.circle.thickness + data.outlineThickness * 2;
        ctx.strokeStyle = '#000000';
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(cx, cy, data.circle.radius, 0, Math.PI * 2);
      ctx.lineWidth = data.circle.thickness;
      ctx.strokeStyle = data.color;
      ctx.stroke();
    }

    // Center dot (classic + dot option)
    if (data.style === 'classic' && data.dot) {
      if (data.outline) {
        ctx.beginPath();
        ctx.arc(cx, cy, data.dot.radius + data.outlineThickness, 0, Math.PI * 2);
        ctx.fillStyle = '#000000';
        ctx.fill();
      }
      ctx.beginPath();
      ctx.arc(cx, cy, data.dot.radius, 0, Math.PI * 2);
      ctx.fillStyle = data.color;
      ctx.fill();
    }

    ctx.globalAlpha = 1.0;
  }

  // ---------------------------------------------------------------------------
  // Visual state helpers
  // ---------------------------------------------------------------------------

  private updateModeButtons(mode: CameraMode): void {
    if (mode === 'fpp') {
      this.setButtonActive(this.fppBtn, true);
      this.setButtonActive(this.tppBtn, false);
    } else {
      this.setButtonActive(this.fppBtn, false);
      this.setButtonActive(this.tppBtn, true);
    }
  }

  private updateTPPGroupState(mode: CameraMode): void {
    const group = this.overlay.querySelector('#settings-tpp-group') as HTMLElement;
    if (group) {
      if (mode === 'fpp') {
        group.style.opacity = '0.4';
        group.style.pointerEvents = 'none';
      } else {
        group.style.opacity = '1';
        group.style.pointerEvents = 'auto';
      }
    }
  }

  private updateQualityButtons(quality: GraphicsQuality): void {
    for (const btn of this.qualityBtns) {
      const q = btn.getAttribute('data-quality');
      this.setButtonActive(btn, q === quality);
    }
  }

  private updateShadowButtons(quality: ShadowQuality): void {
    for (const btn of this.shadowBtns) {
      const q = btn.getAttribute('data-shadow');
      this.setButtonActive(btn, q === quality);
    }
  }

  private updateTextureButtons(quality: TextureQuality): void {
    for (const btn of this.textureBtns) {
      const q = btn.getAttribute('data-texture');
      this.setButtonActive(btn, q === quality);
    }
  }

  private updateStyleButtons(style: string): void {
    const btns = this.overlay.querySelectorAll<HTMLElement>('.ch-style-btn');
    for (const btn of btns) {
      this.setButtonActive(btn, btn.getAttribute('data-style') === style);
    }
  }

  private updateColorSelection(color: string): void {
    const btns = this.overlay.querySelectorAll<HTMLElement>('.ch-color-btn');
    for (const btn of btns) {
      const isMatch = btn.getAttribute('data-color') === color;
      btn.style.borderColor = isMatch ? '#ffffff' : 'transparent';
    }
  }

  private setButtonActive(el: HTMLElement, active: boolean): void {
    if (active) {
      el.style.background = 'rgba(233,69,96,0.3)';
      el.style.border = '1px solid #e94560';
      el.style.color = '#e94560';
    } else {
      el.style.background = 'rgba(60,60,50,0.4)';
      el.style.border = '1px solid #3a3a2a';
      el.style.color = '#888';
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  show(): void {
    if (this.open) return;
    this.open = true;
    this.overlay.style.display = 'flex';
    this.syncFromSettings();
  }

  /** Re-sync all UI elements from the current settings store values. */
  private syncFromSettings(): void {
    const s = settingsStore.get();

    // Camera
    this.updateModeButtons(s.cameraMode);
    this.updateTPPGroupState(s.cameraMode);

    // Sensitivity
    this.sensitivitySlider.value = String(s.sensitivity);
    this.sensitivityValue.textContent = s.sensitivity.toFixed(4);

    // TPP
    this.distanceSlider.value = String(s.tppDistance);
    this.distanceValue.textContent = s.tppDistance.toFixed(1) + 'm';
    this.heightSlider.value = String(s.tppHeight);
    this.heightValue.textContent = s.tppHeight.toFixed(1) + 'm';
    this.shoulderToggle.textContent = s.tppShoulder >= 0 ? 'RIGHT' : 'LEFT';

    // Audio
    this.masterVolumeSlider.value = String(s.masterVolume);
    this.masterVolumeValue.textContent = Math.round(s.masterVolume * 100) + '%';
    this.sfxVolumeSlider.value = String(s.sfxVolume);
    this.sfxVolumeValue.textContent = Math.round(s.sfxVolume * 100) + '%';
    this.musicVolumeSlider.value = String(s.musicVolume);
    this.musicVolumeValue.textContent = Math.round(s.musicVolume * 100) + '%';
    this.voiceVolumeSlider.value = String(s.voiceVolume);
    this.voiceVolumeValue.textContent = Math.round(s.voiceVolume * 100) + '%';
    this.announcerVolumeSlider.value = String(s.announcerVolume);
    this.announcerVolumeValue.textContent = Math.round(s.announcerVolume * 100) + '%';
    this.footstepVolumeSlider.value = String(s.footstepVolume);
    this.footstepVolumeValue.textContent = Math.round(s.footstepVolume * 100) + '%';
    // BUG-P-16: refresh mic device list each time the panel opens so newly
    // plugged-in mics show up even if `devicechange` fired while it was closed.
    if (this.micDeviceSelect) void this.populateMicDevices();

    // Graphics
    this.updateQualityButtons(s.graphicsQuality);

    // Accessibility
    this.colorblindSelect.value = s.colorblindMode;
    if (this.adsSnapToggle) this.adsSnapToggle.textContent = s.adsSnapEnabled ? 'ON' : 'OFF';
    if (this.controlModeSelect) this.controlModeSelect.value = s.controlMode;

    // Theme dropdown
    const themeSelect = this.overlay.querySelector('#settings-theme-select') as HTMLSelectElement | null;
    if (themeSelect) themeSelect.value = s.theme;

    // Crosshair
    this.updateStyleButtons(s.crosshair.style);
    this.updateColorSelection(s.crosshair.color);
    this.syncCrosshairSliders(s.crosshair);
    this.renderCrosshairPreview();

    // Extended settings (FOV, shadow, texture, minimap)
    this.extendedSettings = loadExtendedSettings();
    this.fovSlider.value = String(this.extendedSettings.fov);
    this.fovValue.textContent = String(this.extendedSettings.fov);
    this.updateShadowButtons(this.extendedSettings.shadowQuality);
    this.updateTextureButtons(this.extendedSettings.textureQuality);
    this.minimapOpacitySlider.value = String(this.extendedSettings.minimapOpacity);
    this.minimapOpacityValue.textContent = this.extendedSettings.minimapOpacity.toFixed(1);
    this.minimapScaleSlider.value = String(this.extendedSettings.minimapScale);
    this.minimapScaleValue.textContent = this.extendedSettings.minimapScale.toFixed(1);
    this.minimapTeammatesCheckbox.checked = this.extendedSettings.minimapShowTeammates;

    // Viewport info
    const viewportInfo = this.overlay.querySelector('#settings-viewport-info');
    if (viewportInfo && typeof window !== 'undefined') {
      viewportInfo.textContent = window.innerWidth + ' x ' + window.innerHeight;
    }

    // Clear any prior search filter so all sections are visible on re-open.
    const searchInput = this.overlay.querySelector('#settings-search-input') as HTMLInputElement | null;
    if (searchInput) {
      searchInput.value = '';
      this.applySectionSearchFilter('');
    }
  }

  /**
   * BUG-P-16: enumerate audioinput devices and populate the mic picker.
   * Browsers gate device labels behind mic permission, so an empty-label list
   * is treated the same as a permission-denied throw: surface the inline note
   * with a Retry button that re-prompts via getUserMedia({audio: true}).
   */
  private async populateMicDevices(): Promise<void> {
    const select = this.micDeviceSelect;
    const note = this.micPermissionNote;
    if (!select) return;

    const showNote = (visible: boolean) => {
      if (note) note.style.display = visible ? 'flex' : 'none';
    };

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      // No MediaDevices API at all — leave the default option and hide the note.
      showNote(false);
      return;
    }

    let devices: MediaDeviceInfo[] = [];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch {
      showNote(true);
      return;
    }

    const inputs = devices.filter((d) => d.kind === 'audioinput');
    // Empty list OR all labels blank → permission not granted yet.
    const hasLabels = inputs.length > 0 && inputs.some((d) => d.label.length > 0);
    if (!hasLabels) {
      showNote(true);
    } else {
      showNote(false);
    }

    const saved = settingsStore.get().micDeviceId;
    // Rebuild options. Always keep "System default" first.
    select.innerHTML = '<option value="">System default</option>';
    let foundSaved = !saved;
    for (let i = 0; i < inputs.length; i++) {
      const dev = inputs[i];
      const opt = document.createElement('option');
      opt.value = dev.deviceId;
      opt.textContent = dev.label || `Microphone ${i + 1}`;
      if (dev.deviceId === saved) {
        opt.selected = true;
        foundSaved = true;
      }
      select.appendChild(opt);
    }
    // Saved deviceId no longer exists (e.g. headset unplugged) → fall back to default.
    if (!foundSaved) {
      select.value = '';
    }
  }

  private syncCrosshairSliders(ch: CrosshairConfig): void {
    const set = (id: string, val: string) => {
      const el = this.overlay.querySelector(`#${id}`) as HTMLInputElement | null;
      if (el) el.value = val;
    };
    const setText = (id: string, text: string) => {
      const el = this.overlay.querySelector(`#${id}`) as HTMLElement | null;
      if (el) el.textContent = text;
    };
    const setCb = (id: string, checked: boolean) => {
      const el = this.overlay.querySelector(`#${id}`) as HTMLInputElement | null;
      if (el) el.checked = checked;
    };

    set('settings-ch-size-slider', String(ch.size));
    setText('settings-ch-size-val', ch.size + 'px');
    set('settings-ch-gap-slider', String(ch.gap));
    setText('settings-ch-gap-val', ch.gap + 'px');
    set('settings-ch-thick-slider', String(ch.thickness));
    setText('settings-ch-thick-val', ch.thickness + 'px');
    set('settings-ch-alpha-slider', String(ch.alpha));
    setText('settings-ch-alpha-val', Math.round(ch.alpha * 100) + '%');
    setCb('settings-ch-outline', ch.outline);
    setCb('settings-ch-dot', ch.dot);
    setCb('settings-ch-dynamic', ch.dynamic);

    const picker = this.overlay.querySelector('#settings-ch-color-custom') as HTMLInputElement | null;
    if (picker) picker.value = ch.color;
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    this.overlay.style.display = 'none';
  }

  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }

  isOpen(): boolean {
    return this.open;
  }

  setOnReplayMobileTutorial(callback: (() => void) | null): void {
    this.onReplayMobileTutorial = callback;
  }

  /** F-MOB-H1: register a callback that requests gyro permission (must be
   *  called from a user gesture). Returns true on success. */
  setOnRequestGyroPermission(cb: (() => Promise<boolean> | boolean) | null): void {
    this.onRequestGyroPermission = cb;
  }

  /** F-MOB-H1: register a callback that resets gyro orientation to zero. */
  setOnCalibrateGyro(cb: (() => void) | null): void {
    this.onCalibrateGyro = cb;
  }

  /** MOB-W5 / F-MOB-M5: register a callback that opens the HUD layout editor. */
  setOnOpenHudEditor(cb: (() => void) | null): void {
    this.onOpenHudEditor = cb;
  }

  /** MOB-W6: register a getter that returns the current quick-buy loadout
   *  (3 weapon ids). Called by renderQuickBuySlot each time the menu opens
   *  so the dropdowns reflect the live state. */
  setQuickBuyLoadoutGetter(getter: (() => string[]) | null): void {
    this.getQuickBuyLoadout = getter;
  }

  /** MOB-W6: register a callback fired when a single quick-buy slot
   *  changes via the dropdown. The host should write through
   *  QuickBuyController.saveLoadout and refresh the on-screen button
   *  labels. */
  setOnQuickBuySlotChange(cb: ((slot: 0 | 1 | 2, weaponId: string) => void) | null): void {
    this.onQuickBuySlotChange = cb;
  }

  dispose(): void {
    document.removeEventListener('keydown', this.boundKeyHandler, true);
    this.keybindEditor?.dispose();
    this.keybindEditor = null;
    if (this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
  }
}
